import { trust, founders, cta } from '../content';
import { usePageMeta, Swipe, Button, Arrow, SmartLink } from '../ui';
import { BookingWidget } from '../booking/BookingWidget';
import { ShareButtons } from './industries/Share';
import { TrustFacts } from './trust/Visuals';

// One plain reading per pledge, keyed by content.trust.pledges[].t. Each keeps its legal source (p.src / p.href).
const PLEDGE: Record<string, string> = {
  'You own your data':
    'Records, conversations, AI outputs and customer details are yours. Export it all, free, any time.',
  'AI built to serve you, not trained on your IP':
    'It improves only your own assistant, never a shared model. It cannot approve or refuse a claim, and you can switch it off.',
  'Codes you control':
    'On your own domain you control the code for as long as the product is in use.',
  'Built for GDPR and EU rules':
    'A DPA in the terms, an Article 27 EU representative, every sub-processor published. Data Act, AI Act and passport rules designed in.',
  'Products in the field come first':
    'Public passports and product pages stay up, even in an account dispute.',
};

const NOT_YET = [
  { k: 'Cyber Essentials (UK)', v: 'Planned' },
  { k: 'Independent penetration test', v: 'Planned. Summary under NDA once done' },
  { k: 'ISO/IEC 27001', v: 'Not certified. Aligning to 27001:2022' },
  { k: 'SOC 2', v: 'Not held. Our cloud provider’s covers only their controls' },
  { k: 'Single sign-on (SSO)', v: 'Not offered yet' },
];

const IT = [
  { k: 'Hosting', v: 'EU cloud regions with a geo-redundant copy.' },
  { k: 'Encryption', v: 'TLS 1.2+ with HSTS in transit, AES-256 at rest, secrets in a vault.' },
  { k: 'Sign-in', v: 'Passkeys or two-factor for staff, enforceable by admins. Owners never use a password.' },
  { k: 'Your ERP', v: 'Stays the master. Native Epicor Kinetic connector; others by API.' },
  { k: 'Your data', v: 'DPA in the terms, sub-processors published with 30 days’ notice. Free export, up to 30 days after the end.' },
  { k: 'Availability', v: '99.5% monthly uptime target, public status page. Breach notice aimed at 48 hours, 72 at most.' },
];

const MD = [
  'Each product gets an AI expert that answers owners from our manuals, any hour.',
  'Set-up, help, parts and repair from one scan, in our brand.',
  'Parts, accessories and cover, sold to the owner of that exact unit.',
  'We finally know who owns what, so the next product is better.',
  'Start with one product line. The data stays ours.',
];



export default function WhyTrust() {
  usePageMeta(
    'Why trust us: your data, your customers, your brand',
    'Your brand, powered by us. You own the data, the customers and what your AI learns, with GDPR built into the terms.'
  );

  const itBody = (url: string) =>
    `Hi,\n\nWe are looking at BrandedMark for product registration and aftersales. Before we go further, could you look over their security and IT position?\n\n${IT.map((r) => `- ${r.k}: ${r.v}`).join('\n')}\n\nThey publish what is not in place yet (Cyber Essentials and a pen test are planned; no ISO 27001, SOC 2 or SSO), and answer security questionnaires under NDA.\n\nTrust Centre: https://www.brandedmark.com/trust/\nStatus: https://status.brandedmark.com\nSummary: ${url}\n`;
  const mdBody = (url: string) =>
    `Hi,\n\nThe short version on BrandedMark, the supplier I mentioned:\n\n${MD.map((m) => `- ${m}`).join('\n')}\n\nMore here:\n${url}\n`;

  return (
    <div className="trust-page">
      {/* Hero */}
      <section className="wrap pb-16 pt-14 sm:pt-20">
        <h1 className="t-h1 trust-h1 max-w-[20ch]">
          {trust.title[0]} <Swipe draw>{trust.title[1]}</Swipe>
        </h1>
        <div className="mt-12 grid gap-12 lg:grid-cols-[0.72fr_1.28fr] lg:gap-12">
          <div>
            <p className="t-lede max-w-[46ch]">{trust.lede}</p>
            <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Button href={`${cta.primaryHref}?src=why-trust-us`}>
                {cta.primary} <Arrow />
              </Button>
              <a href={cta.trustHref} className="border-b border-bm-line pb-px text-[15.5px] font-medium text-bm-muted hover:border-bm-indigo hover:text-bm-indigo">
                or open the Trust Centre
              </a>
            </div>
          </div>
          <TrustFacts />
        </div>
      </section>

      {/* Pledges: one line each, each with the clause that binds us */}
      <section className="border-t border-bm-line bg-bm-alt">
        <div className="wrap section">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
            <h2 className="t-h2 max-w-[15ch]">
              We do what <Swipe>we say.</Swipe>
            </h2>
          </div>
          <ol className="mt-14 grid border-t border-bm-indigo md:grid-cols-2 md:gap-x-14">
            {trust.pledges.map((p, i) => (
              <li key={p.t} className="grid grid-cols-[32px_1fr] gap-3 border-b border-bm-line py-7">
                <span className="pt-1 text-[15px] font-semibold text-bm-emerald">{i + 1}</span>
                <div>
                  <h3 className="text-[21px] font-bold leading-snug tracking-[-0.02em] text-bm-indigo">{p.t}</h3>
                  <p className="mt-2 text-[16px] leading-relaxed text-bm-ink">{PLEDGE[p.t] ?? p.d}</p>
                  <SmartLink href={p.href} className="mt-3.5 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-[13.5px] font-medium text-bm-indigo ring-1 ring-bm-line hover:ring-bm-indigo">
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                      <path d="M4 1.8h5.2L12.5 5v9.2H4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M6.3 8.2h4M6.3 10.8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    {p.src}
                  </SmartLink>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Forwardable: IT and the MD, each a card they can be sent */}
      <section>
        <div className="wrap section">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
            <div>
              <span className="bar" />
              <h2 className="t-h2 max-w-[16ch]">
                Get <Swipe>buy-in.</Swipe>
              </h2>
            </div>
          </div>
          <div className="mt-14 grid gap-5 lg:grid-cols-[1.12fr_1fr]">
            <div className="flex flex-col rounded-[22px] p-7 ring-1 ring-bm-line sm:p-9">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h3 className="t-h3">IT speak</h3>
              </div>
              <dl className="mt-6 divide-y divide-bm-line border-y border-bm-line">
                {IT.map((r) => (
                  <div key={r.k} className="grid gap-1 py-3.5 sm:grid-cols-[130px_1fr] sm:gap-5">
                    <dt className="text-[14.5px] font-semibold text-bm-indigo">{r.k}</dt>
                    <dd className="text-[15px] text-bm-ink">{r.v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[14px] font-medium">
                <a href="/legal/subprocessors/" className="text-bm-emerald hover:underline">Sub-processors</a>
                <a href="https://status.brandedmark.com" className="text-bm-emerald hover:underline" rel="noopener">Status page</a>
                <a href="/.well-known/security.txt" className="text-bm-emerald hover:underline">security.txt</a>
                <a href="/trust/" className="text-bm-emerald hover:underline">Trust Centre</a>
              </div>
              <ShareButtons className="mt-auto pt-8" label="Email this to IT" subject="Security and IT summary: BrandedMark" body={itBody} />
            </div>
            <div className="on-dark flex flex-col rounded-[22px] bg-bm-indigo p-7 sm:p-9">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <h3 className="t-h3">The CEO pitch</h3>
              </div>
              <ul className="mt-6 space-y-4">
                {MD.map((m) => (
                  <li key={m} className="flex gap-3 text-[16px] leading-relaxed text-white/90">
                    <span className="mt-[11px] h-[4px] w-4 shrink-0 rounded-full" style={{ background: 'var(--bm-gradient)' }} aria-hidden="true" />
                    {m}
                  </li>
                ))}
              </ul>
              <div className="mt-8 border-t border-white/15 pt-6">
                <div className="text-[14.5px] font-semibold text-bm-lime">{founders.eyebrow}</div>
                <ul className="mt-3 space-y-2 text-[15px] text-white/80">
                  {founders.credentials.map((c) => (
                    <li key={c.k}>{c.v}</li>
                  ))}
                </ul>
                <p className="mt-5 text-[13px] leading-relaxed text-white/55">
                  Branded Mark Limited, England and Wales, company 14452041. ICO registration ZB954818.
                </p>
              </div>
              <ShareButtons className="mt-auto pt-8" dark label="Email this to the CEO" subject="Worth a look: BrandedMark" body={mdBody} />
            </div>
          </div>
          <div className="mt-5 rounded-[22px] bg-bm-alt p-7 sm:p-9">
            <h3 className="text-[17px] font-bold tracking-[-0.015em] text-bm-indigo">Planned</h3>
            <ul className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-5">
              {NOT_YET.map((r) => (
                <li key={r.k} className="border-t border-bm-line pt-3">
                  <span className="flex items-center gap-2 text-[14.5px] font-semibold text-bm-indigo">
                    <span className="h-2 w-2 shrink-0 rounded-full border-[1.5px] border-[#B8862B]" aria-hidden="true" />
                    {r.k}
                  </span>
                  <span className="mt-1 block text-[14px] leading-snug text-bm-ink">{r.v}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-[14px] text-bm-muted">
              Security questionnaire, architecture and recovery summary under NDA from legal@brandedmark.com.
            </p>
          </div>
        </div>
      </section>

      {/* Book */}
      <section className="on-dark bg-bm-deep">
        <div className="wrap py-20 sm:py-28">
          <div className="grid gap-6 lg:grid-cols-[1fr_1fr] lg:items-end">
            <h2 className="t-h2 max-w-[16ch]">
              Ask us the <Swipe>awkward questions.</Swipe>
            </h2>
            <p className="t-lede max-w-[46ch] lg:justify-self-end">Bring your IT lead and your toughest procurement checklist.</p>
          </div>
          <div className="mt-12 min-w-0">
            <BookingWidget compact source="why-trust-us" />
          </div>
        </div>
      </section>

      <style>{`
        .v2 .trust-h1 { font-size: clamp(38px, 4.9vw, 68px); line-height: 1.02; }
              `}</style>
    </div>
  );
}
